import { ChevronLeftTwoTone, ChevronRightTwoTone } from "@mui/icons-material";
import { Box, Stack, styled } from "@mui/material";
import { BoxProps } from "@mui/system";
import {
  FC,
  PropsWithChildren,
  ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

interface CarouselProps<T, E> extends PropsWithChildren {
  gap?: number;
  items: T[];
  itemWidth?: { xs: string; sm?: string; md?: string; lg?: string; xl?: string };
  loading?: boolean;
  sx?: BoxProps["sx"];
  Item: FC<CarouselItemProps<T, E>>;
  ItemExtraProps: E;
  Placeholder?: ReactElement;
}

export interface CarouselItemProps<T, E> extends PropsWithChildren {
  i: number;
  value: T;
  extraProps: E;
}

interface CarouselItemContainerProps<T, E> {
  key: string;
  value: T;
  ItemExtraProps: E;
  Item: FC<CarouselItemProps<T, E>>;
  i: number;
  scrollContainer: HTMLDivElement | null;
}

const defaultWidths = {
  xs: "calc(min(100vw, 400px) - 90px)",
  sm: "min(calc(100vw / 2), 300px)",
  md: "min(calc(100vw / 3), 300px)",
  lg: "min(calc(100vw / 4), 300px)",
  xl: "min(calc(100vw / 6), 300px)",
};

export function Carousel<T extends { key: string }, E>({
  loading = false,
  items,
  Item,
  ItemExtraProps,
  itemWidth: CarouselItemWidth = defaultWidths,
  Placeholder,
  sx = [],
  gap = 0,
}: CarouselProps<T, E>): React.JSX.Element {
  const NavigationButton = styled("div")(() => ({
    position: "absolute",
    top: 0,
    height: "100%",
    width: "var(--carouselOffset)",
    cursor: "pointer",
    display: "grid",
    color: "grey",
    backgroundColor: "#33333366",
    ":hover": {
      backgroundColor: "#333333aa",
      color: "white",
    },
    placeItems: "center",
    transition: "background-color 0.2s linear",
    zIndex: 100,
  }));

  const NavigationIcon = styled("div")(() => ({
    borderRadius: "2em",
    backgroundColor: "#333333dd",
    width: "calc(var(--carouselOffset) - 1em)",
    height: "calc(var(--carouselOffset) - 1em)",
    display: "grid",
    placeItems: "center",
    position: "absolute",
  }));

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const container = useRef<HTMLDivElement>(null);

  const calculateCanScrollRight = (): boolean => {
    if (!container.current) return false;

    return (
      container.current.scrollLeft < container.current.scrollWidth - container.current.offsetWidth
    );
  };

  const handleWindowSizeChange = useCallback(() => {
    setCanScrollRight(calculateCanScrollRight());
  }, []);

  useEffect(() => {
    setCanScrollRight(calculateCanScrollRight());

    window.addEventListener("resize", handleWindowSizeChange);
    return () => {
      window.removeEventListener("resize", handleWindowSizeChange);
    };
  }, [handleWindowSizeChange]);

  const handleScroll = useCallback(() => {
    if (!container.current) return;

    setCanScrollLeft(container.current.scrollLeft > 0);
    setCanScrollRight(calculateCanScrollRight());
  }, [container]);

  const scroll = useCallback(
    (direction: "left" | "right") => () => {
      if (!container.current || !container.current.offsetParent) return;
      const scrollAmount = container.current.offsetParent.clientWidth;

      container.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    },
    [],
  );

  useEffect(() => {
    handleScroll();
  }, [items, handleScroll]);

  return (
    <Box
      sx={[
        { position: "relative", "--carouselOffset": "4em" },
        ...(sx instanceof Array ? sx : [sx]),
      ]}
      className="Carousel-root"
    >
      <Stack
        direction="row"
        onScroll={handleScroll}
        ref={container}
        className="Carousel-scrollContainer"
        gap={gap}
        sx={{
          position: "relative",
          paddingTop: "15px",
          "--carouselItemWidth": CarouselItemWidth,
          width: "100%",
          display: "grid",
          gridTemplateColumns: `repeat(${items.length}, var(--carouselItemWidth))`,
          overflowClipMargin: "15px",
          overflowY: "hidden",
          overflowX: "scroll",
          overflowClipBox: "padding-box",
          overscrollBehaviorX: "contain",
          alignItems: "stretch",
          msOverflowStyle: "none" /* Internet Explorer 10+ */,
          scrollbarWidth: "none" /* Firefox */,
          "::-webkit-scrollbar": {
            display: "none" /* Safari and Chrome */,
          },
        }}
      >
        {!loading &&
          items.map((item, i) => (
            <CarouselItemContainer
              key={item.key}
              value={item}
              ItemExtraProps={ItemExtraProps}
              Item={Item}
              i={i}
              scrollContainer={container.current}
            />
          ))}
        {items.length === 0 && Placeholder}
      </Stack>
      {canScrollLeft && (
        <NavigationButton
          onClick={scroll("left")}
          sx={{ left: "0" }}
          className="Carousel-navigation Carousel-navigation--left"
        >
          <NavigationIcon className="Carousel-navigationIcon">
            <ChevronLeftTwoTone fontSize="large" />
          </NavigationIcon>
        </NavigationButton>
      )}
      {canScrollRight && (
        <NavigationButton
          onClick={scroll("right")}
          sx={{ right: "0" }}
          className="Carousel-navigation Carousel-navigation--right"
        >
          <NavigationIcon className="Carousel-navigationIcon">
            <ChevronRightTwoTone fontSize="large" />
          </NavigationIcon>
        </NavigationButton>
      )}
    </Box>
  );
}

function CarouselItemContainer<T, E>({
  value,
  Item,
  ItemExtraProps,
  i,
  scrollContainer,
}: CarouselItemContainerProps<T, E>): ReactElement {
  const ref = useRef<HTMLDivElement>(null);
  const observer = useRef<IntersectionObserver | null>(null);
  const [intersecting, setIntersecting] = useState(false);

  useEffect(() => {
    observer.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIntersecting((prev) => (!prev ? entry.isIntersecting : prev));
        });
      },
      {
        root: scrollContainer,
      },
    );

    observer.current?.observe(ref.current!);
  }, [i, scrollContainer]);

  return (
    <Box
      className="Carousel-item"
      sx={{
        ":hover": {
          zIndex: 10,
        },
      }}
      ref={ref}
    >
      {intersecting && <Item value={value} i={i} extraProps={ItemExtraProps} />}
    </Box>
  );
}
